import { useCurrentBox } from "@modules/box/useBox"
import { useServicesContext } from "@modules/core/services/services.context"
import { FinanceStatisticsEntity } from "@modules/finances/entities/financeStatisticsEntity"
import { TransactionDetailsEntity } from "@modules/finances/entities/transactionDetailsEntity"
import { TransactionEntity } from "@modules/finances/entities/transactionEntity"
import { TransactionStatus } from "@modules/finances/types/financesType"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"

export const useTransactions = (size = 20, period: string, page?: string, status?: TransactionStatus) => {
  const { userFinanceApi, boxFinanceApi } = useServicesContext()
  const { result: boxId } = useCurrentBox()
  const financeApi = boxId ? boxFinanceApi : userFinanceApi
  const apiKey = boxId ? "boxTransactions" : "userTransactions"

  const { data, isLoading } = useQuery({
    queryKey: [apiKey, period, size, page, status],
    queryFn: async () => {
      return await financeApi.getTransactions(status, period, page, size)
    },
  })

  return {
    data: data?.data.map((transactionDto) => TransactionEntity.fromDto(transactionDto)) || [],
    isLoading,
    hasMore: data?.hasMore || false,
    nextPage: data?.nextPage || undefined,
  }
}

export const useTransactionDetails = (transactionId: string) => {
  const { userFinanceApi, boxFinanceApi } = useServicesContext()
  const { result: boxId } = useCurrentBox()
  const financeApi = boxId ? boxFinanceApi : userFinanceApi
  const apiKey = boxId ? "boxTransactionDetails" : "userTransactionDetails"

  const { data, error, isLoading } = useQuery({
    queryKey: [apiKey, transactionId],
    queryFn: async () => {
      return await financeApi.getTransaction(transactionId)
    },
  })

  return {
    data: data ? TransactionDetailsEntity.fromDto(data) : null,
    error,
    isLoading,
  }
}

export const useFinanceLastUpdate = () => {
  const { boxFinanceApi, userFinanceApi } = useServicesContext()
  const { result: boxId } = useCurrentBox()
  const financeApi = boxId ? boxFinanceApi : userFinanceApi
  const apiKey = boxId ? "boxLastUpdate" : "userLastUpdate"
  const queryClient = useQueryClient()

  function invalidateKeys() {
    const currentDate = dayjs().format("YYYY-MM")
    if (boxId) {
      queryClient.invalidateQueries({
        queryKey: ["boxTransactions", currentDate],
      })
      queryClient.invalidateQueries({
        queryKey: ["boxTransactionTotals", currentDate],
      })
    } else {
      queryClient.invalidateQueries({
        queryKey: ["userTransactions", currentDate],
      })
      queryClient.invalidateQueries({
        queryKey: ["userTransactionTotals", currentDate],
      })
    }
  }

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: [apiKey],
    queryFn: async () => {
      const data = await financeApi.getLastUpdate()
      if (data) {
        invalidateKeys()
      }
      return data
    },
  })

  return {
    data: data ? dayjs(data) : undefined,
    isLoading,
    refetch,
    isRefetching,
  }
}

export const useFinanceFirstPeriod = () => {
  const { boxFinanceApi, userFinanceApi } = useServicesContext()
  const { result: boxId } = useCurrentBox()
  const financeApi = boxId ? boxFinanceApi : userFinanceApi
  const apiKey = boxId ? "boxFirstPeriod" : "userFirstPeriod"

  const { data, isLoading, refetch } = useQuery({
    queryKey: [apiKey],
    queryFn: async () => {
      return await financeApi.getFirstPeriod()
    },
  })

  return {
    data,
    isLoading,
    refetch,
  }
}

export const useFinanceStats = (enabled: boolean, period: string) => {
  const { boxFinanceApi, userFinanceApi } = useServicesContext()
  const { result: boxId } = useCurrentBox()
  const financeApi = boxId ? boxFinanceApi : userFinanceApi
  const apiKey = boxId ? "boxStats" : "userStats"

  const queryClient = useQueryClient()

  const {
    data,
    isLoading,
    refetch,
    isRefetching: isRefetchingQuery,
    error,
  } = useQuery({
    queryKey: [apiKey, period],
    queryFn: async () => {
      return await financeApi.getStats(period)
    },
    enabled,
  })

  const { mutateAsync: updateStats, isPending } = useMutation({
    mutationFn: async () => {
      return await financeApi.updateStats()
    },
    onSuccess: (data) => {
      queryClient.setQueryData([apiKey, period], data ? FinanceStatisticsEntity.fromDto(data) : undefined)
    },
  })

  const isRefetching = isRefetchingQuery || isPending

  return {
    data: data ? FinanceStatisticsEntity.fromDto(data) : undefined,
    isLoading,
    isRefetching,
    error,
    refetch,
    updateStats: async () => {
      await updateStats()
    },
  }
}
